// components/AudioManager.jsx
import { useEffect, useRef, useCallback, useState } from 'react';

export const AudioManager = ({ timerState, localRemainingTime, competitionWarningTime }) => {
    // Audio refs
    const warningAudioRef = useRef(null);
    const countdownAudioRef = useRef(null);

    // State refs
    const isPlayingRef = useRef(false);
    const lastPlayedSecondRef = useRef(-1);
    const audioTimeoutRef = useRef(null);
    const audioLoopRef = useRef(null);
    const userInteractedRef = useRef(false);
    const lastAudioPhaseRef = useRef('');
    const warningPhaseTypeRef = useRef(''); // 'first5' or 'last5'

    // Initialize audio
    useEffect(() => {
        // Create audio instances
        warningAudioRef.current = new Audio("/beep-warning-6387.mp3");
        countdownAudioRef.current = new Audio("/short-beep-countdown-81121.mp3");

        // Configure audio
        [warningAudioRef.current, countdownAudioRef.current].forEach(audio => {
            audio.preload = 'auto';
            audio.volume = 0.7;
            audio.muted = false;
            audio.loop = false;
        });

        // Preload
        warningAudioRef.current.load();
        countdownAudioRef.current.load();

        // Add user interaction listeners to enable audio
        const enableAudio = () => {
            userInteractedRef.current = true;
            console.log('✅ User interaction detected - audio enabled');

            // Try to unmute audio
            [warningAudioRef.current, countdownAudioRef.current].forEach(audio => {
                if (audio) {
                    audio.muted = false;
                    // Try to play and immediately pause to unlock audio
                    const playPromise = audio.play();
                    if (playPromise !== undefined) {
                        playPromise
                            .then(() => {
                                audio.pause();
                                audio.currentTime = 0;
                            })
                            .catch(e => {
                                console.log('Audio unlock play failed:', e);
                            });
                    }
                }
            });

            // Remove listeners after first interaction
            document.removeEventListener('click', enableAudio);
            document.removeEventListener('touchstart', enableAudio);
            document.removeEventListener('keydown', enableAudio);
        };

        // Add event listeners for user interaction
        document.addEventListener('click', enableAudio, { once: true });
        document.addEventListener('touchstart', enableAudio, { once: true });
        document.addEventListener('keydown', enableAudio, { once: true });

        return () => {
            stopAllAudio();
            if (warningAudioRef.current) {
                warningAudioRef.current.pause();
                warningAudioRef.current = null;
            }
            if (countdownAudioRef.current) {
                countdownAudioRef.current.pause();
                countdownAudioRef.current = null;
            }

            // Clean up event listeners
            document.removeEventListener('click', enableAudio);
            document.removeEventListener('touchstart', enableAudio);
            document.removeEventListener('keydown', enableAudio);
        };
    }, []);

    // Stop all audio
    const stopAllAudio = useCallback(() => {
        if (warningAudioRef.current) {
            warningAudioRef.current.pause();
            warningAudioRef.current.currentTime = 0;
        }
        if (countdownAudioRef.current) {
            countdownAudioRef.current.pause();
            countdownAudioRef.current.currentTime = 0;
        }

        isPlayingRef.current = false;
        lastPlayedSecondRef.current = -1;
        lastAudioPhaseRef.current = '';
        warningPhaseTypeRef.current = '';

        if (audioTimeoutRef.current) {
            clearTimeout(audioTimeoutRef.current);
            audioTimeoutRef.current = null;
        }

        if (audioLoopRef.current) {
            clearInterval(audioLoopRef.current);
            audioLoopRef.current = null;
        }
    }, []);

    // Play single beep (for countdown)
    const playCountdownBeep = useCallback(() => {
        if (!countdownAudioRef.current || isPlayingRef.current || !userInteractedRef.current) {
            console.log('Cannot play countdown:', {
                hasAudio: !!countdownAudioRef.current,
                isPlaying: isPlayingRef.current,
                userInteracted: userInteractedRef.current
            });
            return;
        }

        isPlayingRef.current = true;
        lastPlayedSecondRef.current = localRemainingTime;
        lastAudioPhaseRef.current = 'countdown';

        console.log('🎵 Playing countdown beep at second:', localRemainingTime);

        // Ensure audio is ready
        countdownAudioRef.current.currentTime = 0;
        countdownAudioRef.current.muted = false;

        const playPromise = countdownAudioRef.current.play();

        if (playPromise !== undefined) {
            playPromise
                .then(() => {
                    console.log('✅ Countdown audio started');
                    // Reset after audio finishes
                    audioTimeoutRef.current = setTimeout(() => {
                        console.log('🔄 Countdown audio finished, resetting state');
                        isPlayingRef.current = false;
                        lastAudioPhaseRef.current = '';
                    }, countdownAudioRef.current.duration * 1000);
                })
                .catch(error => {
                    console.log('❌ Countdown audio play failed:', error);
                    isPlayingRef.current = false;
                    lastAudioPhaseRef.current = '';
                });
        }
    }, [localRemainingTime]);

    // Play warning audio with loop - updated to handle different contexts
    const playWarningLoop = useCallback((phaseType = 'first5') => {
        if (!warningAudioRef.current || isPlayingRef.current || !userInteractedRef.current) {
            console.log('Cannot play warning:', {
                hasAudio: !!warningAudioRef.current,
                isPlaying: isPlayingRef.current,
                userInteracted: userInteractedRef.current
            });
            return;
        }

        isPlayingRef.current = true;
        lastPlayedSecondRef.current = localRemainingTime;
        lastAudioPhaseRef.current = 'warning';
        warningPhaseTypeRef.current = phaseType;

        console.log(`🎵 Starting warning loop at second: ${localRemainingTime}, phase type: ${phaseType}`);

        let playCount = 0;
        const maxPlays = 2;

        const playNext = () => {
            if (playCount >= maxPlays) {
                console.log('✅ Warning loop completed');
                stopAllAudio();
                return;
            }

            warningAudioRef.current.currentTime = 0;
            warningAudioRef.current.muted = false;

            const playPromise = warningAudioRef.current.play();

            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                        playCount++;
                        console.log(`✅ Warning audio play ${playCount}/${maxPlays}`);
                        
                        // Check if we should continue looping based on the phase type
                        let shouldContinue = false;
                        
                        if (phaseType === 'first5') {
                            // For first 5 seconds of warning phase, continue if still in first 5 seconds
                            const isStillInFirst5Seconds = localRemainingTime <= competitionWarningTime && 
                                                          localRemainingTime > (competitionWarningTime - 5);
                            shouldContinue = playCount < maxPlays && isStillInFirst5Seconds;
                        } else if (phaseType === 'last5') {
                            // For last 5 seconds (wrap_time or countdown), continue if still in last 5 seconds
                            shouldContinue = playCount < maxPlays && localRemainingTime <= 5 && localRemainingTime > 0;
                        }
                        
                        if (shouldContinue) {
                            console.log('⏳ Scheduling next warning audio');
                            audioTimeoutRef.current = setTimeout(playNext, 3000);
                        } else {
                            console.log('🔄 Warning loop stopping');
                            stopAllAudio();
                        }
                    })
                    .catch(error => {
                        console.log('❌ Warning audio play failed:', error);
                        stopAllAudio();
                    });
            }
        };

        // Start the loop
        playNext();
    }, [localRemainingTime, stopAllAudio, competitionWarningTime]);

    // Manage audio based on timer state
    useEffect(() => {
        if (!timerState || localRemainingTime <= 0) {
            stopAllAudio();
            return;
        }

        const { currentPhase } = timerState;
        const warningTime = competitionWarningTime || 30; // Note: Changed default to 30 based on your logs

        console.log('🔊 Audio Manager Check:', {
            currentPhase,
            localRemainingTime,
            warningTime,
            userInteracted: userInteractedRef.current,
            isPlaying: isPlayingRef.current,
            lastPlayedSecond: lastPlayedSecondRef.current,
            lastAudioPhase: lastAudioPhaseRef.current
        });

        // Only play audio for active phases
        if (!['countdown', 'climbing', 'wrap_time', 'warning'].includes(currentPhase)) {
            stopAllAudio();
            return;
        }

        // Don't play if we already played for this second in the same phase
        if (lastPlayedSecondRef.current === localRemainingTime && 
            lastAudioPhaseRef.current === currentPhase) {
            console.log('⏸️ Already played audio for this second in current phase');
            return;
        }

        // Don't play if user hasn't interacted yet
        if (!userInteractedRef.current) {
            console.log('🔇 Audio disabled - waiting for user interaction');
            return;
        }

        switch (currentPhase) {
            case 'countdown':
                // Last 5 seconds of countdown
                if (localRemainingTime <= 5 && localRemainingTime > 0) {
                    console.log('🔊 Playing countdown warning audio');
                    playWarningLoop('last5');
                } else {
                    stopAllAudio();
                }
                break;

            case 'climbing':
                // NO audio during climbing phase
                stopAllAudio();
                break;

            case 'warning':
                console.log('⚠️ Warning phase detailed check:', {
                    localRemainingTime,
                    warningTime,
                    firstCondition: localRemainingTime <= warningTime && localRemainingTime > warningTime - 5,
                    firstConditionValues: `${localRemainingTime} <= ${warningTime} && ${localRemainingTime} > ${warningTime - 5}`,
                    secondCondition: localRemainingTime <= 5 && localRemainingTime > 0,
                    secondConditionValues: `${localRemainingTime} <= 5 && ${localRemainingTime} > 0`
                });
                
                // First 5 seconds of warning phase (e.g., 30-25 seconds)
                const isFirst5SecondsOfWarning = localRemainingTime <= warningTime && 
                                               localRemainingTime > (warningTime - 5);
                
                // Last 5 seconds of warning phase
                const isLast5Seconds = localRemainingTime <= 5 && localRemainingTime > 0;
                
                if (isFirst5SecondsOfWarning) {
                    console.log('🔊 Playing warning phase START audio (first 5 seconds)');
                    playWarningLoop('first5');
                }
                else if (isLast5Seconds) {
                    console.log('🔊 Playing warning phase END audio (last 5 seconds)');
                    playCountdownBeep();
                } else {
                    stopAllAudio();
                }
                break;

            case 'wrap_time':
                // Last 5 seconds of wrap time
                if (localRemainingTime <= 5 && localRemainingTime > 0) {
                    console.log('🔊 Playing wrap time audio');
                    playWarningLoop('last5');
                } else {
                    stopAllAudio();
                }
                break;

            default:
                stopAllAudio();
        }
    }, [
        timerState,
        localRemainingTime,
        competitionWarningTime,
        stopAllAudio,
        playWarningLoop,
        playCountdownBeep
    ]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopAllAudio();
        };
    }, [stopAllAudio]);

    // This component doesn't render anything
    return null;
};